import type { Attrs } from './attrs'
import type { _createMetricsFactory } from './tel-metrics'

export type ApmMetricsFactory = ReturnType<typeof _createMetricsFactory>
export type ApmCounter = ReturnType<ApmMetricsFactory['counter']>
export type ApmHistogram = ReturnType<ApmMetricsFactory['histogram']>

const DEFAULT_APM_AGGREGATION_INTERVAL_MS = 60_000

export function getApmRequestCounter(
  metrics: ApmMetricsFactory,
  counters: Map<string, ApmCounter>,
  endpoint: string,
  failed: boolean,
  aggregationInterval = DEFAULT_APM_AGGREGATION_INTERVAL_MS,
) {
  const key = JSON.stringify([endpoint, failed])
  const existing = counters.get(key)
  if (existing) return existing
  const created = metrics.counter({
    name: 'requests',
    unit: 'requests',
    attributes: apmMetricAttrs(endpoint, { failed }),
    aggregationInterval,
  })
  counters.set(key, created)
  return created
}

export function getApmDurationHistogram(
  metrics: ApmMetricsFactory,
  histograms: Map<string, ApmHistogram>,
  endpoint: string,
  aggregationInterval = DEFAULT_APM_AGGREGATION_INTERVAL_MS,
) {
  return getApmHistogram(
    metrics,
    histograms,
    endpoint,
    'request.duration',
    'ms',
    aggregationInterval,
  )
}

export function getApmNormalizedDurationHistogram(
  metrics: ApmMetricsFactory,
  histograms: Map<string, ApmHistogram>,
  endpoint: string,
  aggregationInterval = DEFAULT_APM_AGGREGATION_INTERVAL_MS,
) {
  return getApmHistogram(
    metrics,
    histograms,
    endpoint,
    'request.normalized_duration',
    'ms_per_kib',
    aggregationInterval,
  )
}

function getApmHistogram(
  metrics: ApmMetricsFactory,
  histograms: Map<string, ApmHistogram>,
  endpoint: string,
  name: string,
  unit: string,
  aggregationInterval: number,
) {
  const key = JSON.stringify([endpoint])
  const existing = histograms.get(key)
  if (existing) return existing
  const created = metrics.histogram({
    name,
    unit,
    attributes: apmMetricAttrs(endpoint),
    aggregationInterval,
  })
  histograms.set(key, created)
  return created
}

export function apmMetricAttrs(
  endpoint: string,
  extra: { failed?: boolean } = {},
): Attrs {
  const attrs: Attrs = { endpoint }
  if (extra.failed !== undefined) attrs.failed = extra.failed
  return attrs
}
